import { store } from '../store';
import { handleSessionExpired } from './sessionHandler';
import { IPasswordEntry, IDecryptedPasswordEntry, ICreatePasswordEntry, IVaultResponse, IVaultCountsResponse } from '../types/vault.types';
import { encryptData, decryptData, encryptKeyData, decryptKeyData } from '../utils/encryption';
import { hybridDecrypt, importPrivateKey } from '../utils/rsaCrypto';
import { config } from '../config';
import { secureStore } from '../utils/secureStore';
import { NotesService } from '../services/notes.service';
import { CardDetailsService } from '../services/cardDetails.service';
import { BankDetailsService } from '../services/bankDetails.service';
import { PersonalDetailsService } from '../services/personalDetails.service';
import { Note } from '../types/notes';
import { CardDetail } from '../types/cardDetails';
import { BankDetail } from '../types/bankDetails';
import { PersonalDetail } from '../types/personalDetails';

const API_URL = config.API_URL;
const EXTENSION_ID = config.EXTENSION_ID;

export class VaultService {
    private baseUrl: string;
    // Removed authToken; authentication now via session cookie

    constructor() {
        this.baseUrl = API_URL;
    }

    // Removed setAuthToken; authentication now via session cookie

    private static async request<T>(
        endpoint: string,
        method: string = 'GET',
        body?: any
    ): Promise<T> {
        // No token needed; authentication via session cookie

        try {
            const response = await fetch(`${API_URL}/vault${endpoint}`, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: body ? JSON.stringify(body) : undefined,
                credentials: 'include', // Send cookies for session auth
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 401) {
                    handleSessionExpired();
                }
                throw new Error(data.message || 'An error occurred');
            }

            return data;
        } catch (error) {
            console.error('API request failed:', error);
            throw error;
        }
    }

    static async getAllEntries(workspaceId?: string): Promise<IDecryptedPasswordEntry[]> {
        // Ensure encryption keys are loaded (especially important in extension context)
        await secureStore.ensureKeysLoaded();
        
        // Fetch and cache vault-level keys for v2 shared vaults
        await this.fetchAndCacheVaultKeys();
        
        let endpoint = '/entries';
            
        // Process the workspaceId parameter
        if (workspaceId) {
            endpoint += `?workspace_id=${workspaceId}`;
        }
        
        const entries = await this.request<IPasswordEntry[]>(endpoint);
        
        const decryptedEntries = this.decryptEntries(entries);
        
        // Save to extension storage
        // if (chrome.runtime && EXTENSION_ID) {
        //     chrome.runtime.sendMessage(
        //         EXTENSION_ID,
        //         { 
        //             type: 'SAVE_ENTRIES',
        //             payload: decryptedEntries 
        //         },
        //         (response) => {
        //             if (chrome.runtime.lastError) {
        //                 console.error('Failed to save entries to extension:', chrome.runtime.lastError);
        //             } else {
        //                 console.log('Entries saved to extension storage');
        //             }
        //         }
        //     );
        // }
        
        return decryptedEntries;
    }

    static async getFavoriteEntries(): Promise<IDecryptedPasswordEntry[]> {
        const entries = await this.request<IPasswordEntry[]>('/entries/favorites');
        return this.decryptEntries(entries);
    }

    static async createEntry(entry: ICreatePasswordEntry, workspaceId?: string): Promise<IDecryptedPasswordEntry> {
        try {
            const encryptedEntry = this.encryptEntry(entry);
            
            // Add workspace_id to the encrypted entry object
            if (workspaceId) {
                encryptedEntry.workspace_id = workspaceId;
            }
            
            const response = await this.request<IPasswordEntry>('/entries', 'POST', encryptedEntry);
            const decryptedEntry = this.decryptEntry(response);
            
            // Refresh entries in extension storage
            const allEntries = await this.getAllEntries(workspaceId);
            
            return decryptedEntry;
        } catch (error) {
            console.error('Create entry error:', error);
            throw error;
        }
    }

    static async updateEntry(
        id: string, 
        entry: Partial<ICreatePasswordEntry>,
        workspaceId?: string
    ): Promise<IDecryptedPasswordEntry> {
        const encryptedEntry = this.encryptEntry(entry);
        
        // Add workspace_id to the encrypted entry object
        if (workspaceId) {
            encryptedEntry.workspace_id = workspaceId;
        }


        const response = await this.request<IPasswordEntry>(`/entries/${id}`, 'PUT', encryptedEntry);
        return this.decryptEntry(response);
    }

    static async deleteEntry(id: string, isShared = false, sharedKey?: string): Promise<void> {
        try {
            // Include isShared in query params if the entry is shared
            const queryParams = isShared ? `?isShared=true` : '';
            const url = `${API_URL}/vault/entries/${id}${queryParams}`;
            // If we have a shared key, add it to the request body
            const options: RequestInit = {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            };
            if (sharedKey) {
                options.body = JSON.stringify({ sharedKey });
            }
            const response = await fetch(url, options);
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to delete entry');
            }
            
            return;
        } catch (error) {
            console.error('Error deleting entry:', error);
            throw error;
        }
    }

    static async searchEntries(query: string): Promise<IDecryptedPasswordEntry[]> {
        const entries = await this.request<IPasswordEntry[]>(`/entries/search?q=${encodeURIComponent(query)}`);
        return this.decryptEntries(entries);
    }

    static async getEntryById(id: string, sharedKey?: string, permissionLevel?: string): Promise<IDecryptedPasswordEntry> {
        const entry = await this.request<IPasswordEntry>(`/entries/${id}`);
        return this.decryptEntry(entry, sharedKey, permissionLevel);
    }

    static async getAllItemsData(workspaceId?: string): Promise<{
        passwords: IDecryptedPasswordEntry[];
        notes: Note[];
        cards: CardDetail[];
        banks: BankDetail[];
        personals: PersonalDetail[];
    }> {
        // Process the workspaceId parameter for the endpoint
        let endpoint = '/all-items-data';
        
        if (workspaceId) {
            if (workspaceId.startsWith('personal-')) {
                // For personal workspace, we want to fetch entries with null workspace_id
                endpoint += '?workspace_id=null';
            } else {
                // For team workspaces, pass the ID as is
                endpoint += `?workspace_id=${workspaceId}`;
            }
        }
            
        const response = await this.request<IVaultResponse>(endpoint);
        

        // Check if the response indicates success
        if (!response.success) {
            throw new Error('Failed to fetch items');
        }

        const data = response.data; // Access the nested data object

        try {
            // Check if passwords are defined and is an array
            const decryptedPasswords = Array.isArray(data.passwords) ? this.decryptEntries(data.passwords) : [];
            // Decrypt notes
            const decryptedNotes = Array.isArray(data.notes) ? data.notes.map(note => {
                return {
                    ...note,
                    encrypted_notes: this.decryptBufferData(note.encrypted_notes)
                };
            }) : [];
            
            // Decrypt cards
            const decryptedCards = Array.isArray(data.cards) ? data.cards.map(card => {
                return {
                    ...card,
                    encrypted_card_holder_name: this.decryptBufferData(card.encrypted_card_holder_name),
                    encrypted_card_number: this.decryptBufferData(card.encrypted_card_number),
                    encrypted_expiration_date: this.decryptBufferData(card.encrypted_expiration_date),
                    encrypted_cvv: this.decryptBufferData(card.encrypted_cvv),
                    encrypted_pin: card.encrypted_pin ? this.decryptBufferData(card.encrypted_pin) : null,
                    encrypted_postal_code: card.encrypted_postal_code ? this.decryptBufferData(card.encrypted_postal_code) : null,
                    encrypted_notes: card.encrypted_notes ? this.decryptBufferData(card.encrypted_notes) : null
                };
            }) : [];
            
            // Decrypt banks
            const decryptedBanks = Array.isArray(data.banks) ? data.banks.map(bank => {
                return {
                    ...bank,
                    encrypted_routing_number: bank.encrypted_routing_number ? this.decryptBufferData(bank.encrypted_routing_number) : null,
                    encrypted_account_number: bank.encrypted_account_number ? this.decryptBufferData(bank.encrypted_account_number) : null,
                    encrypted_swift_code: bank.encrypted_swift_code ? this.decryptBufferData(bank.encrypted_swift_code) : null,
                    encrypted_iban_number: bank.encrypted_iban_number ? this.decryptBufferData(bank.encrypted_iban_number) : null,
                    encrypted_pin: bank.encrypted_pin ? this.decryptBufferData(bank.encrypted_pin) : null,
                    encrypted_notes: bank.encrypted_notes ? this.decryptBufferData(bank.encrypted_notes) : null
                };
            }) : [];
            
            // Decrypt personals
            const decryptedPersonals = Array.isArray(data.personals) ? data.personals.map(personal => {
                return {
                    ...personal,
                    encrypted_first_name: this.decryptBufferData(personal.encrypted_first_name),
                    encrypted_middle_name: this.decryptBufferData(personal.encrypted_middle_name),
                    encrypted_last_name: this.decryptBufferData(personal.encrypted_last_name),
                    encrypted_username: this.decryptBufferData(personal.encrypted_username),
                    encrypted_email_address: this.decryptBufferData(personal.encrypted_email_address),
                    encrypted_phone: this.decryptBufferData(personal.encrypted_phone),
                    encrypted_notes: personal.encrypted_notes ? this.decryptBufferData(personal.encrypted_notes) : null
                };
            }) : [];

            return {
                passwords: decryptedPasswords,
                notes: decryptedNotes,
                cards: decryptedCards,
                banks: decryptedBanks,
                personals: decryptedPersonals as PersonalDetail[]
            };
        } catch (error) {
            console.error('Decryption error:', error);
            throw new Error('Failed to decrypt items');
        }
    }

    static async getItemCounts(workspaceId?: string): Promise<{
        passwords: number;
        cards: number;
        banks: number;
        personals: number;
    }> {
        let endpoint = '/items-counts';

        if (workspaceId) {
            if (workspaceId.startsWith('personal-')) {
                endpoint += '?workspace_id=null';
            } else {
                endpoint += `?workspace_id=${workspaceId}`;
            }
        }

        const response = await this.request<IVaultCountsResponse>(endpoint);

        if (!response.success) {
            throw new Error('Failed to fetch vault counts');
        }

        const data = response.data || {};

        return {
            passwords: data.passwords ?? 0,
            cards: data.cards ?? 0,
            banks: data.banks ?? 0,
            personals: data.personals ?? 0
        };
    }

    static async getFavouriteItemsData(workspaceId?: string): Promise<{
        passwords: IDecryptedPasswordEntry[];
        notes: Note[];
        cards: CardDetail[];
        banks: BankDetail[];
        personals: PersonalDetail[];
    }> {
        // Process the workspaceId parameter for the endpoint
        let endpoint = '/favourites-items-data';
        
        if (workspaceId) {
            if (workspaceId.startsWith('personal-')) {
                // For personal workspace, we want to fetch entries with null workspace_id
                endpoint += '?workspace_id=null';
            } else {
                // For team workspaces, pass the ID as is
                endpoint += `?workspace_id=${workspaceId}`;
            }
        }
            
        const response = await this.request<IVaultResponse>(endpoint);
        

        // Check if the response indicates success
        if (!response.success) {
            throw new Error('Failed to fetch items');
        }

        const data = response.data; // Access the nested data object

        try {
            // Check if passwords are defined and is an array
            const decryptedPasswords = Array.isArray(data.passwords) ? this.decryptEntries(data.passwords) : [];
            // Decrypt notes
            const decryptedNotes = Array.isArray(data.notes) ? data.notes.map(note => {
                return {
                    ...note,
                    encrypted_notes: this.decryptBufferData(note.encrypted_notes)
                };
            }) : [];
            
            // Decrypt cards
            const decryptedCards = Array.isArray(data.cards) ? data.cards.map(card => {
                return {
                    ...card,
                    encrypted_card_holder_name: this.decryptBufferData(card.encrypted_card_holder_name),
                    encrypted_card_number: this.decryptBufferData(card.encrypted_card_number),
                    encrypted_expiration_date: this.decryptBufferData(card.encrypted_expiration_date),
                    encrypted_cvv: this.decryptBufferData(card.encrypted_cvv),
                    encrypted_pin: card.encrypted_pin ? this.decryptBufferData(card.encrypted_pin) : null,
                    encrypted_postal_code: card.encrypted_postal_code ? this.decryptBufferData(card.encrypted_postal_code) : null,
                    encrypted_notes: card.encrypted_notes ? this.decryptBufferData(card.encrypted_notes) : null
                };
            }) : [];
            
            // Decrypt banks
            const decryptedBanks = Array.isArray(data.banks) ? data.banks.map(bank => {
                return {
                    ...bank,
                    encrypted_routing_number: bank.encrypted_routing_number ? this.decryptBufferData(bank.encrypted_routing_number) : null,
                    encrypted_account_number: bank.encrypted_account_number ? this.decryptBufferData(bank.encrypted_account_number) : null,
                    encrypted_swift_code: bank.encrypted_swift_code ? this.decryptBufferData(bank.encrypted_swift_code) : null,
                    encrypted_iban_number: bank.encrypted_iban_number ? this.decryptBufferData(bank.encrypted_iban_number) : null,
                    encrypted_pin: bank.encrypted_pin ? this.decryptBufferData(bank.encrypted_pin) : null,
                    encrypted_notes: bank.encrypted_notes ? this.decryptBufferData(bank.encrypted_notes) : null
                };
            }) : [];
            
            // Decrypt personals
            const decryptedPersonals = Array.isArray(data.personals) ? data.personals.map(personal => {
                return {
                    ...personal,
                    encrypted_first_name: this.decryptBufferData(personal.encrypted_first_name),
                    encrypted_middle_name: this.decryptBufferData(personal.encrypted_middle_name),
                    encrypted_last_name: this.decryptBufferData(personal.encrypted_last_name),
                    encrypted_username: this.decryptBufferData(personal.encrypted_username),
                    encrypted_email_address: this.decryptBufferData(personal.encrypted_email_address),
                    encrypted_phone: this.decryptBufferData(personal.encrypted_phone),
                    encrypted_notes: personal.encrypted_notes ? this.decryptBufferData(personal.encrypted_notes) : null
                };
            }) : [];

            return {
                passwords: decryptedPasswords,
                notes: decryptedNotes,
                cards: decryptedCards,
                banks: decryptedBanks,
                personals: decryptedPersonals as PersonalDetail[]
            };
        } catch (error) {
            console.error('Decryption error:', error);
            throw new Error('Failed to decrypt items');
        }
    }

    static async getPasswordsNotInThisVault(categoryId: string): Promise<IDecryptedPasswordEntry[]> {
        const response = await this.request<IPasswordEntry[]>(`/passwords-not-in-this-vault/${categoryId}`);
        return this.decryptEntries(response);
    }

    private static getMasterKey(): string {
        try {
            return secureStore.getVaultKey();
        } catch (error) {
            throw new Error('Vault key not found. Please login again.');
        }
    }

    public static encryptEntry(entry: Partial<ICreatePasswordEntry>): any {
        try {
            if (!entry.title || !entry.username || !entry.password) {
                throw new Error('Missing required fields');
            }

            // Determine which encryption key to use
            let encryptionKey: string;
            if (entry.isSharedUpdate && entry.sharedKey) {
                // For shared entries, use the provided shared key
                encryptionKey = entry.sharedKey;
            } else {
                // For personal entries, use the vault key
                encryptionKey = this.getMasterKey();
            }

            let encryptedUsername, encryptedPassword, encryptedNotes;
            
            try {
                encryptedUsername = encryptData(entry.username, encryptionKey);
            } catch (error) {
                console.error('Username encryption failed:', error);
                throw new Error('Failed to encrypt username');
            }

            try {
                encryptedPassword = encryptData(entry.password, encryptionKey);
            } catch (error) {
                console.error('Password encryption failed:', error);
                throw new Error('Failed to encrypt password');
            }

            if (entry.notes) {
                try {
                    encryptedNotes = encryptData(entry.notes, encryptionKey);
                } catch (error) {
                    console.error('Notes encryption failed:', error);
                    encryptedNotes = null;
                }
            }

            // IMPORTANT: Always set shared_key for ALL password entries
            // - For new items: Encrypt owner's vault key with SECRET_KEY
            // - For shared items: Use the provided sharedKey
            // - For updates: Use existing sharedKey or vault key
            // This ensures consistency and enables sharing/password reset
            let finalSharedKey = '';
            if (entry.sharedKey) {
                // Use provided shared key (for updates or already-shared items)
                finalSharedKey = encryptKeyData(entry.sharedKey);
            } else if (!entry.isSharedUpdate) {
                // For NEW items or personal items, encrypt vault key with SECRET_KEY
                // This allows the item to be shared later and enables password reset tracking
                finalSharedKey = encryptKeyData(encryptionKey);
            }

            return {
                title: entry.title,
                encrypted_username: encryptedUsername,
                encrypted_password: encryptedPassword,
                encrypted_notes: encryptedNotes,
                website_url: entry.website_url || '',
                // Backward compatibility - send primary category as category_id
                category_id: entry.primary_category_id || entry.category_id || '',
                // New multi-category support
                category_ids: entry.category_ids || [],
                primary_category_id: entry.primary_category_id || '',
                favourite: entry.favourite || false,
                auto_login: entry.auto_login || false,
                isSharedUpdate: entry.isSharedUpdate || false,
                shared_key: finalSharedKey,
                itemType: entry.itemType || 'password',
                favicon_url: entry.favicon_url || '',
                password_strength: entry.password_strength || '',
                remove_category_ids: entry.remove_category_ids || []
            };
        } catch (error) {
            console.error('Encryption error:', error);
            throw new Error('Failed to encrypt password entry');
        }
    }

    private static calculatePasswordStrength(password: string, username?: string): { score: number; label: string } {
        let score = 0;
        
        // Handle null or undefined password
        if (!password) {
            return { score: 0, label: 'Very Weak' };
        }

        // Length check
        if (password.length >= 12) {
            score += 20;
        }

        // Number check
        if (/\d/.test(password)) {
            score += 20;
        }

        // Lowercase check
        if (/[a-z]/.test(password)) {
            score += 20;
        }

        // Uppercase check
        if (/[A-Z]/.test(password)) {
            score += 20;
        }

        // Special character check
        if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            score += 20;
        }

        // Email check - if username is available and in the password
        if (username && password.toLowerCase().includes(username.toLowerCase())) {
            score -= 20;
        }

        // Get strength label
        let label = 'Very Weak';
        if (score <= 20) label = 'Very Weak';
        else if (score <= 40) label = 'Weak';
        else if (score <= 60) label = 'Fair';
        else if (score <= 80) label = 'Strong';
        else label = 'Very Strong';

        return { score, label };
    }

    /**
     * Fetch and decrypt vault-level keys for v2 shared vaults.
     * Caches decrypted keys in secureStore to avoid repeated decryption.
     */
    private static async fetchAndCacheVaultKeys(): Promise<void> {
        try {
            // Fetch vault keys and encrypted private key from backend
            const response = await this.request<any>('/vault-keys');
            
            if (!response || !response.vaultKeys || response.vaultKeys.length === 0) {
                return;
            }


            // Get encrypted private key from response
            const encryptedPrivateKeyData = response.encryptedPrivateKey;

            if (!encryptedPrivateKeyData) {
                console.warn('VaultService: No encrypted private key available');
                return;
            }

            // Decrypt private key using encryption key from secureStore
            const encryptionKey = secureStore.getEncryptionKey();
            const { loadPrivateKey } = await import('../utils/rsaCrypto');
            const privateKey = await loadPrivateKey(encryptedPrivateKeyData, encryptionKey);

            // Decrypt and cache each vault key
            for (const vaultKey of response.vaultKeys) {
                const { vaultId, encryptedData, encryptedKey, iv, authTag, cryptoVersion } = vaultKey;

                // Only process v2 keys
                if (cryptoVersion !== 2) {
                    continue;
                }

                // Ensure vaultId is a string (UUID)
                const vaultIdStr = String(vaultId);
                
                if (!vaultIdStr) {
                    console.warn(`VaultService: Invalid vault ID: ${vaultId}`);
                    continue;
                }

                // Check if already cached
                if (secureStore.hasVaultLevelKey(vaultIdStr)) {
                    continue;
                }

                try {
                    // Decrypt vault key using RSA hybrid decryption
                    const decryptedKey = await hybridDecrypt(
                        encryptedData,
                        encryptedKey,
                        iv,
                        privateKey,
                        authTag
                    );

                    // Cache the decrypted vault key
                    secureStore.setVaultLevelKey(vaultIdStr, decryptedKey);
                } catch (error) {
                    console.error(`VaultService: Failed to decrypt vault key for vault ${vaultIdStr}:`, error);
                }
            }
        } catch (error) {
            console.error('VaultService: Failed to fetch vault keys:', error);
            // Don't throw - continue with existing cached keys
        }
    }

    public static decryptEntry(entry: IPasswordEntry, sharedKey?: string, permissionLevel?: string): IDecryptedPasswordEntry {
        let decryptionKey = '';
        
        // Determine which key to use for decryption
        // Priority: vault-level key (v2) > shared_key (v1 individual) > personal vault key
        const vaultId = entry.vault_id ? String(entry.vault_id) : null;
        if (vaultId && secureStore.hasVaultLevelKey(vaultId)) {
            // V2: Use vault-level key for shared vault items
            const vaultKey = secureStore.getVaultLevelKey(vaultId);
            if (vaultKey) {
                decryptionKey = vaultKey;
            } else {
                console.warn(`Vault key not found for vault ${vaultId}, falling back`);
                decryptionKey = sharedKey || this.getMasterKey();
            }
        } else if (entry.shared_key) {
            // V1: Individual item sharing with SECRET_KEY wrapping
            decryptionKey = decryptKeyData(entry.shared_key);
        } else {
            // Personal vault item
            decryptionKey = sharedKey || this.getMasterKey();
        }
        
        try {
            const decryptBufferData = (encryptedBuffer: any): string => {
                if (!encryptedBuffer) return '';
                
                try {
                    if (encryptedBuffer.data && Array.isArray(encryptedBuffer.data)) {
                        const uint8Array = new Uint8Array(encryptedBuffer.data);
                        const encryptedString = new TextDecoder().decode(uint8Array);
                        return decryptData(encryptedString, decryptionKey);
                    }
                    
                    if (typeof encryptedBuffer === 'string') {
                        return decryptData(encryptedBuffer, decryptionKey);
                    }

                    return '';
                } catch (error) {
                    console.error('Error decrypting buffer:', error);
                    return '';
                }
            };
            
            const username = decryptBufferData(entry.encrypted_username);
            const password = decryptBufferData(entry.encrypted_password);

            
            // Determine password strength
            let passwordStrength = entry.password_strength;
            if (!passwordStrength) {
                // Calculate strength if not stored in database
                const strengthResult = this.calculatePasswordStrength(password, username);
                passwordStrength = strengthResult.label;
            }

            const decryptedData: IDecryptedPasswordEntry = {
                id: entry.id,
                vault_id: entry.vault_id,
                title: entry.title,
                username: username,
                password: password,
                notes: entry.encrypted_notes ? 
                    decryptBufferData(entry.encrypted_notes) : undefined,
                website_url: entry.website_url || '',
                // Backward compatibility
                category_id: entry.category_id || '',
                category_name: entry.category_name || '',
                // Multi-category support - use categories array if available, otherwise create from single category
                categories: entry.categories && entry.categories.length > 0 
                    ? entry.categories 
                    : (entry.category_id ? [{
                        id: entry.category_id,
                        name: entry.category_name || '',
                        is_primary: true
                    }] : []),
                favourite: entry.favourite,
                last_used: entry.last_used ? new Date(entry.last_used) : undefined,
                password_strength: passwordStrength,
                created_at: new Date(entry.created_at),
                updated_at: new Date(entry.updated_at),
                auto_login: entry.auto_login || false,
                personal_vault_id: entry.personal_vault_id || '',
                favicon_url: entry.favicon_url || '',
                shared_key: entry.shared_key ? decryptKeyData(entry.shared_key) : '',
                permission_level: entry.permission_level || permissionLevel || 'write',
                is_owner: entry.is_owner || false
            };
            
            return decryptedData;
        } catch (error) {
            console.error('Decryption error:', error);
            throw new Error('Failed to decrypt password entry');
        }
    }

    private static decryptEntries(entries: IPasswordEntry[]): IDecryptedPasswordEntry[] {
        return entries.map(entry => {
            try {
                return this.decryptEntry(entry);
            } catch (error) {
                console.error(`Failed to decrypt entry ${entry.id}:`, error);
                // Return a placeholder for failed entries
                return {
                    id: entry.id,
                    vault_id: entry.vault_id,
                    title: entry.title,
                    username: '(Decryption failed)',
                    password: '(Decryption failed)',
                    notes: undefined,
                    website_url: entry.website_url || '',
                    category_id: entry.category_id || '',
                    category_name: entry.category_name || '',
                    favourite: entry.favourite,
                    last_used: entry.last_used ? new Date(entry.last_used) : undefined,
                    password_strength: entry.password_strength || '',
                    created_at: new Date(entry.created_at),
                    updated_at: new Date(entry.updated_at),
                    auto_login: false,
                    personal_vault_id: entry.personal_vault_id || '',
                    favicon_url: entry.favicon_url || '',
                    is_owner: entry.is_owner || false
                };
            }
        });
    }

    static async toggleFavorite(entryId: string): Promise<void> {
        await this.request(`/entries/${entryId}/favourite`, 'POST');
    }

    static async addCategoryToEntry(entryId: string, categoryId: string): Promise<void> {
        await this.request(`/entry/${entryId}/category`, 'POST', { categoryId });
    }

    static async removeCategoryFromEntry(entryId: string): Promise<void> {
        await this.request(`/entry/${entryId}/category`, 'DELETE');
    }

    private static decryptBufferData(encryptedBuffer: any, isShared: boolean = false, sharedKey?: string): string {
        if (!encryptedBuffer) return '';

        try {
            if (encryptedBuffer.data && Array.isArray(encryptedBuffer.data)) {
                const uint8Array = new Uint8Array(encryptedBuffer.data);
                const encryptedString = new TextDecoder().decode(uint8Array);
                return isShared && sharedKey ? 
                    decryptData(encryptedString, sharedKey) : 
                    decryptData(encryptedString, this.getMasterKey());
            }

            if (typeof encryptedBuffer === 'string') {
                return isShared && sharedKey ? 
                    decryptData(encryptedBuffer, sharedKey) : 
                    decryptData(encryptedBuffer, this.getMasterKey());
            }

            return '';
        } catch (error) {
            console.error('Error decrypting buffer:', error);
            return '';
        }
    }
}

export const vaultService = new VaultService();